import React, { useState } from 'react';
import { Building2, Mail, Lock, Phone, MapPin, Globe2, Calendar, Award, ChevronDown, Check, X, CheckCircle } from 'lucide-react';
import { collegeSignup } from '../../services/api';
import TermsModal from './TermsModal'; // Ensure this path is correct

// Success Popup Component
const SuccessPopup = ({ isDarkMode, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full rounded-2xl shadow-2xl p-8 text-center animate-in zoom-in ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
          <CheckCircle className="w-12 h-12 text-white" />
        </div>
        
        <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          College Registration Successful!
        </h2>
        
        <p className={`text-base mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          We've sent a verification email to your inbox. Please check your email and click the verification link to activate your college account.
        </p>
        
        <div className={`p-4 rounded-lg mb-6 ${
          isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
        }`}>
          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            📧 <strong>Check your spam folder</strong> if you don't see the email within a few minutes.
          </p>
        </div>
        
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all"
        >
          Got it!
        </button>
      </div>
    </div>
  );
};

export default function College({ isDarkMode }) {
  const [formData, setFormData] = useState({
    collegeName: '',
    universityName: '',
    email: '',
    password: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    establishedYear: '',
    accreditationType: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // New State for Terms Modal & Checkbox
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // --- Validation Start ---
    if (!formData.collegeName.trim()) {
      setError('College name is required');
      return;
    }
    if (!formData.universityName.trim()) {
      setError('University name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // STRICT PASSWORD VALIDATION
    const password = formData.password;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLengthValid = password.length >= 8;

    if (!isLengthValid || !hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setError('Password must be 8+ chars with 1 Uppercase, 1 Lowercase, 1 Number & 1 Special Char (e.g., Example123@)');
      return;
    }

    if (!formData.mobile.trim()) {
      setError('Mobile number is required');
      return;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return;
    }
    // TERMS VALIDATION
    if (!termsAccepted) {
      setError('You must accept the Terms & Conditions to proceed.');
      return;
    }
    // --- Validation End ---

    setIsSubmitting(true);

    try {
      await collegeSignup(formData);

      setShowSuccessPopup(true);

      setFormData({
        collegeName: '',
        universityName: '',
        email: '',
        password: '',
        mobile: '',
        address: '',
        city: '',
        state: '',
        establishedYear: '',
        accreditationType: '',
      });
      setTermsAccepted(false); // Reset terms checkbox

    } catch (err) {
      console.error('College Signup Error:', err);
      if (err.response?.status === 409) {
        setError('An account with this email already exists. Please login instead.');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Signup failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.collegeName && formData.universityName && 
                      formData.email && formData.password && formData.mobile && 
                      formData.address && formData.city && formData.state;

  return (
    <>
      {/* Terms Modal Component */}
      <TermsModal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
        onAgree={() => {
          setTermsAccepted(true);
          setIsTermsOpen(false);
        }}
        isDarkMode={isDarkMode} 
      />

      {showSuccessPopup && (
        <SuccessPopup 
          isDarkMode={isDarkMode} 
          onClose={() => {
            setShowSuccessPopup(false);
            window.location.href = '/login?type=college';
          }} 
        />
      )}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm flex items-start gap-2">
          <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {/* College Name */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            College Name <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Building2 className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="collegeName"
              required
              value={formData.collegeName}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter college name"
            />
          </div>
        </div>

        {/* University Name */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            University Name <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Globe2 className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="universityName"
              required
              value={formData.universityName}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter university name"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Email Address <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="college@example.com"
            />
          </div>
        </div>

        {/* Mobile */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="tel"
              name="mobile"
              required
              value={formData.mobile}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="+91 9876543210"
            />
          </div>
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Address <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <MapPin className={`absolute left-3 top-3 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <textarea
              name="address"
              required
              value={formData.address}
              onChange={handleInputChange}
              rows="2"
              className={`w-full pl-11 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter complete address"
            />
          </div>
        </div>

        {/* City */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            City <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <MapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="city"
              required
              value={formData.city}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter city"
            />
          </div>
        </div>

        {/* State */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            State <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Globe2 className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="state"
              required
              value={formData.state}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter state"
            />
          </div>
        </div>

        {/* Established Year */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Established Year <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <div className="relative group">
            <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="establishedYear"
              value={formData.establishedYear}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="e.g., 1995"
            />
          </div>
        </div>

        {/* Accreditation Type */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Accreditation Type <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <div className="relative group">
            <Award className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <select
              name="accreditationType"
              value={formData.accreditationType}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-10 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">Select accreditation</option>
              <option value="NAAC">NAAC</option>
              <option value="NBA">NBA</option>
              <option value="UGC">UGC</option>
              <option value="AICTE">AICTE</option>
              <option value="Other">Other</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>

        {/* Password */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="password"
              name="password"
              required
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="e.g., Example123@"
            />
            {/* Password Helper Text */}
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char.
            </p>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Checkbox */}
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            I agree to the{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsTermsOpen(true);
              }}
              className="text-blue-600 hover:text-blue-700 font-semibold hover:underline focus:outline-none"
            >
              Terms & Conditions
            </button>
          </span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        disabled={!isFormValid || !termsAccepted || isSubmitting}
        onClick={handleSubmit}
        className="w-full mt-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Registering College...</span>
          </>
        ) : (
          <>
            <Check className="w-5 h-5" />
            <span>Register College</span>
          </>
        )}
      </button>
    </>
  );
}